import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface Cotation {
    societe_symbole: string;
    date_cotation: string;
    cours_ouverture: number;
    cours_cloture: number;
    variation_jour_pct: number;
    volume_echange: number;
    valeur_echange: number;
}

const AdminCotationsScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [cotations, setCotations] = useState<Cotation[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [editingCotation, setEditingCotation] = useState<Cotation | null>(null);

    const fetchCotations = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/cotations?date=${selectedDate}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setCotations(Array.isArray(data) ? data : []);
            } else {
                console.error('Error fetching cotations: HTTP', response.status);
                setCotations([]);
            }
        } catch (error) {
            console.error('Error fetching cotations:', error);
            setCotations([]);
        } finally {
            setLoading(false);
        }
    }, [token, selectedDate]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchCotations();
        }
    }, [token, selectedDate, hasPermission, fetchCotations]);

    const handleEdit = (cotation: Cotation) => {
        setEditingCotation(cotation);
    };

    const handleSave = async () => {
        if (!editingCotation) return;

        try {
            // Endpoint: /v1/admin/cotations/{symbole}/{date_cotation}
            const response = await fetch(`${API_URL}/v1/admin/cotations/${editingCotation.societe_symbole}/${editingCotation.date_cotation}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    cours_ouverture: Number(editingCotation.cours_ouverture),
                    cours_cloture: Number(editingCotation.cours_cloture),
                    variation_jour_pct: Number(editingCotation.variation_jour_pct),
                    volume_echange: Number(editingCotation.volume_echange),
                    valeur_echange: Number(editingCotation.valeur_echange)
                }),
            });

            if (response.ok) {
                fetchCotations();
                setEditingCotation(null);
            } else {
                alert('Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('Error updating cotation:', error);
        }
    };

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé.</p></Box>
            </SiteLayout>
        )
    }

    return (
        <SiteLayout>
            <Header icon='show_chart' title='Gestion des Actions' />
            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p>Cotations du jour</p>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    {loading ? (
                        <p>Chargement...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Symbole</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Ouverture</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Clôture</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Variation</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Volume</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Valeur</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cotations.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                📅 Aucune cotation trouvée pour cette date
                                            </td>
                                        </tr>
                                    ) : cotations.map((cot, index) => (
                                        <tr key={cot.societe_symbole} style={{ backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: 600 }}>{cot.societe_symbole}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>{cot.cours_ouverture}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>{cot.cours_cloture}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right', color: cot.variation_jour_pct >= 0 ? 'green' : 'red' }}>
                                                {cot.variation_jour_pct}%
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>{cot.volume_echange}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>{cot.valeur_echange}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => handleEdit(cot)}
                                                    style={{
                                                        backgroundColor: '#667eea',
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 14px',
                                                        borderRadius: '6px',
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Modifier
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>

            {/* Edit Modal */}
            {editingCotation && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{ background: 'var(--bg-box)', padding: '20px', borderRadius: '8px', width: '400px' }}
                    >
                        <h3>Modifier {editingCotation.societe_symbole} ({editingCotation.date_cotation})</h3>

                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>Cours Ouverture</label>
                            <input
                                type='number'
                                value={editingCotation.cours_ouverture || ''}
                                onChange={(e) => setEditingCotation({ ...editingCotation, cours_ouverture: parseFloat(e.target.value) })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>Cours Clôture</label>
                            <input
                                type='number'
                                value={editingCotation.cours_cloture || ''}
                                onChange={(e) => setEditingCotation({ ...editingCotation, cours_cloture: parseFloat(e.target.value) })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>Variation (%)</label>
                            <input
                                type='number'
                                step="0.01"
                                value={editingCotation.variation_jour_pct || ''}
                                onChange={(e) => setEditingCotation({ ...editingCotation, variation_jour_pct: parseFloat(e.target.value) })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>Volume</label>
                            <input
                                type='number'
                                value={editingCotation.volume_echange || ''}
                                onChange={(e) => setEditingCotation({ ...editingCotation, volume_echange: parseInt(e.target.value) })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 500 }}>Valeur</label>
                            <input
                                type='number'
                                value={editingCotation.valeur_echange || ''}
                                onChange={(e) => setEditingCotation({ ...editingCotation, valeur_echange: parseInt(e.target.value) })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => setEditingCotation(null)} className='button'>
                                Annuler
                            </button>
                            <button onClick={handleSave} className='button button-purple'>
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminCotationsScreen;


